/**
 * Общая оснастка адаптеров канона: контракт, кэш запроса и перевод величин.
 *
 * Зачем адаптер вообще нужен. Экраны Astra написаны против ФОРМ ПРОТОТИПА
 * (`data/types.ts`), и переносить их на канон правкой экранов означало бы
 * переписать прототип заново — то есть потерять именно то, ради чего он
 * рисовался. Поэтому между сервером и экраном стоит перевод: адаптер берёт
 * готовый серверный агрегат и отдаёт ту же форму, которую экран уже умеет
 * читать. Считать на клиенте адаптеру НЕЛЬЗЯ: на районных объёмах свёртка
 * журнала в браузере — это та самая цена, ради отказа от которой сервер и
 * научился отдавать сводку (решение 2 плана `astra-on-canon`).
 *
 * Чего адаптер не делает: он не придумывает поля. Величину, которой канон не
 * называет, адаптер НЕ заполняет — ни нулём, ни пустой строкой (инвариант 5:
 * отсутствие ≠ ноль). Читатель получает `undefined` и обязан сказать «нет
 * данных» словами, а не напечатать ноль.
 */

import { ApiError, type QueryParams } from '../../../api/client';
import { VersionedMemo } from '../../../api/dataEpoch';
import { onPrincipalChange } from '../../../api/session';
import { defineMessages } from '../../../i18n/define';
import { tr } from '../../../i18n/runtime';

const m = defineMessages({
  withCause: {
    id: 'data.refusal.withCause',
    defaultMessage: '{what}: {cause}',
    description: 'Причина отказа сервера для человека; {what} — что не загрузилось («реестр хозяйств»), {cause} — текст ответа сервера',
  },
  noAnswer: {
    id: 'data.refusal.noAnswer',
    defaultMessage: '{what}: сервер не ответил',
    description: 'Причина отказа при сетевом сбое; {what} — что не загрузилось («реестр хозяйств»)',
  },
  setNotBuilt: {
    id: 'data.refusal.setNotBuilt',
    defaultMessage: 'набор «{name}» не собран',
    description: 'Что не загрузилось, когда у сервера не вышло собрать набор данных; {name} — служебное имя набора',
  },
});

/** «набор «…» не собран» — подпись для `refusal`. */
export function setNotBuilt(name: string): string {
  return tr(m.setNotBuilt, { name });
}

/** Один адаптер: какие наборы прототипа он берётся отдать и как. */
export interface DatasetAdapter {
  /** Имя адаптера — для отказов и тестов. Совпадает с именем файла домена. */
  readonly domain: string;
  /** Берётся ли этот адаптер за набор с таким именем. */
  match(name: string): boolean;
  /** Собрать набор. Бросает `ApiError` — обёртку делает источник. */
  load(name: string, params: QueryParams): Promise<unknown>;
}

/* ------------------------------------------------------------ кэш запроса */

const memo = new VersionedMemo<unknown>();

/**
 * Один сетевой запрос на несколько наборов.
 *
 * Экран читает наборы по одному (`cycles.json`, `crops.json`, `meta.json`), а
 * приходят они ИЗ ОДНОГО ответа сервера. Без общей памяти тот же ответ был бы
 * запрошен трижды — и трижды посчитан на сервере. Ключ включает параметры
 * контекста: срез на другую дату — другой ответ, и выдавать вместо него
 * прошлый значило бы соврать про дату.
 *
 * Неудача не запоминается: отказ сервера — состояние минуты, а не свойство
 * набора, и прилипать до перезагрузки страницы он не должен.
 */
export function once<T>(key: string, load: () => Promise<T>): Promise<T> {
  // Память под версией данных (`api/dataEpoch.ts`, agroai-0e8.26, журнал
  // №1626/№1638): внутри навигации — как есть; на следующей навигации и после
  // записи — только если сервер снова подтвердил версию, под которой набор
  // собирался. Без этого собранный набор переживал запись: проверяющий
  // принимал отчёт, возвращался к агроциклам — и видел прежние числа.
  return memo.get(key, load) as Promise<T>;
}

/** Сброс памяти запросов. Нужен тестам и смене аккаунта. */
export function clearApiCache(): void {
  memo.clear();
}

// Ответ срезан областью чтения вошедшего: сменился аккаунт — память не его.
onPrincipalChange(clearApiCache);


/** Ключ параметров контекста — те же пары, что уезжают в адресе запроса. */
export function paramsKey(params: QueryParams): string {
  return Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${key}=${String(value)}`)
    .sort()
    .join('&');
}

/* --------------------------------------------------------------- величины */

/**
 * Микрогектары канона в гектары прототипа. `null` остаётся `undefined`:
 * площадь, которой корпус не знает, не равна нулю гектаров.
 */
export function ha(microha: number | null | undefined): number | undefined {
  if (microha === null || microha === undefined) return undefined;
  return microha / 1_000_000;
}

/** Доля в процентах; знаменатель ноль — величины нет, а не «ноль процентов». */
export function pct(part: number, whole: number): number | undefined {
  if (!whole) return undefined;
  return (part / whole) * 100;
}

/** Текст отказа для человека. Сетевой сбой и отказ сервера звучат по-разному. */
export function refusal(cause: unknown, what: string): string {
  if (cause instanceof ApiError) return tr(m.withCause, { what, cause: cause.message });
  return tr(m.noAnswer, { what });
}
